<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { Line } from 'vue-chartjs'
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend)

const features = [
  {
    title: 'High Performance',
    description: 'Lightning-fast cloud infrastructure designed for maximum efficiency',
    icon: 'fas fa-bolt',
    gradient: 'from-blue-500 to-cyan-500'
  },
  {
    title: 'Global Network',
    description: 'Strategically located data centers for optimal coverage',
    icon: 'fas fa-globe',
    gradient: 'from-purple-500 to-pink-500'
  },
  {
    title: 'Security First',
    description: 'Enterprise-grade security with advanced threat protection',
    icon: 'fas fa-shield-alt',
    gradient: 'from-green-500 to-emerald-500'
  },
  {
    title: '24/7 Support',
    description: 'Round-the-clock expert support whenever you need it',
    icon: 'fas fa-headset',
    gradient: 'from-orange-500 to-red-500'
  }
]

const terminalLines = ref<string[]>([])
const currentLine = ref('')
const commands = [
  'syniac init my-app',
  'Initializing cloud infrastructure...',
  'Setting up compute resources...',
  'Configuring network...',
  'Deploying application...',
  'Setup complete! Your app is live at https://my-app.syniac.com'
]

const typeCommand = async (command: string, index: number) => {
  currentLine.value = ''
  for (const char of command) {
    currentLine.value += char
    await new Promise(resolve => setTimeout(resolve, 50))
  }
  terminalLines.value.push(currentLine.value)
  currentLine.value = ''
  
  if (index < commands.length - 1) {
    setTimeout(() => typeCommand(commands[index + 1], index + 1), 1000)
  }
}

onMounted(() => {
  setTimeout(() => typeCommand(commands[0], 0), 1000)
})

const stats = [
  { label: 'Global Data Centers', value: '11', icon: 'fas fa-server' },
  { label: 'Enterprise Customers', value: '35,000+', icon: 'fas fa-users' },
  { label: 'Uptime SLA', value: '99.99%', icon: 'fas fa-clock' },
  { label: 'Countries Served', value: '150+', icon: 'fas fa-flag' }
]

const performanceData = {
  labels: ['Traditional', 'Syniac'],
  datasets: [
    {
      label: 'Response Time (ms)',
      data: [36, 17],
      backgroundColor: ['#4b5563', '#667eea'],
      borderColor: ['#4b5563', '#667eea'],
      borderWidth: 2,
      borderRadius: 8
    }
  ]
}

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      grid: {
        color: 'rgba(255, 255, 255, 0.1)'
      },
      ticks: {
        color: '#9CA3AF'
      }
    },
    x: {
      grid: {
        color: 'rgba(255, 255, 255, 0.1)'
      },
      ticks: {
        color: '#9CA3AF'
      }
    }
  }
}

const techStack = [
  {
    category: 'Compute',
    items: [
      { name: 'AMD EPYC', description: 'Latest generation processors', icon: 'fas fa-microchip' },
      { name: 'NVIDIA A100', description: 'Enterprise GPU computing', icon: 'fas fa-desktop' },
      { name: 'NVMe Storage', description: 'Ultra-fast storage access', icon: 'fas fa-hdd' }
    ]
  },
  {
    category: 'Network',
    items: [
      { name: 'Global Network', description: 'Optimal network locations', icon: 'fas fa-network-wired' },
      { name: 'Global CDN', description: 'Edge content delivery', icon: 'fas fa-globe-americas' },
      { name: 'DDoS Protection', description: 'Advanced threat mitigation', icon: 'fas fa-shield-alt' }
    ]
  },
  {
    category: 'Security',
    items: [
      { name: 'Zero Trust', description: 'Identity-first security', icon: 'fas fa-lock' },
      { name: 'Encryption', description: 'Data protection at rest & in transit', icon: 'fas fa-key' },
      { name: 'ISO 27001', description: 'Certified security controls', icon: 'fas fa-certificate' }
    ]
  }
]
</script>

<template>
  <div>
    <!-- Hero Section -->
    <section class="relative overflow-hidden hero-gradient min-h-screen flex items-center">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div class="animate-slide-up">
            <div class="modern-badge mb-6">
              <i class="fas fa-rocket mr-2"></i>
              Next-Generation Cloud Platform
            </div>
            <h1 class="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              <span class="block">Cloud Computing</span>
              <span class="block gradient-text">for the Future</span>
            </h1>
            <p class="text-xl text-gray-300 mb-8 leading-relaxed text-balance">
              Experience the next generation of cloud computing with our cutting-edge infrastructure and innovative solutions designed for modern businesses.
            </p>
            <div class="flex flex-col sm:flex-row gap-4">
              <a
                href="https://billing.syniac.com"
                class="btn-primary group"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span>Get Started</span>
                <i class="fas fa-arrow-right ml-2 group-hover:translate-x-1 transition-transform duration-200"></i>
              </a>
              <RouterLink
                to="/contact"
                class="btn-secondary group"
              >
                <span>Contact Sales</span>
                <i class="fas fa-phone ml-2 group-hover:scale-110 transition-transform duration-200"></i>
              </RouterLink>
            </div>
          </div>
          
          <!-- Terminal -->
          <div class="relative animate-fade-scale">
            <div class="glass-card rounded-2xl shadow-modern-lg overflow-hidden">
              <!-- Terminal Header -->
              <div class="flex items-center px-6 py-4 bg-white/5 border-b border-white/10">
                <div class="flex space-x-2">
                  <div class="w-3 h-3 rounded-full bg-red-500"></div>
                  <div class="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <div class="w-3 h-3 rounded-full bg-green-500"></div>
                </div>
                <div class="ml-4 text-sm text-gray-400 font-mono">syniac-cli</div>
              </div>
              <!-- Terminal Content -->
              <div class="p-6 font-mono text-sm min-h-[200px]">
                <div v-for="(line, index) in terminalLines" :key="index" class="text-gray-300 mb-1">
                  <span class="text-primary-400">$</span> {{ line }}
                </div>
                <div v-if="currentLine" class="text-gray-300">
                  <span class="text-primary-400">$</span> {{ currentLine }}
                  <span class="animate-pulse text-primary-400">|</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Stats Section -->
    <section class="py-20 bg-white/5 backdrop-blur-sm">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-8">
          <div v-for="stat in stats" :key="stat.label" class="text-center group">
            <div class="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-secondary-500 mb-4 group-hover:scale-110 transition-transform duration-300">
              <i :class="[stat.icon, 'text-2xl text-white']"></i>
            </div>
            <div class="text-4xl font-bold gradient-text mb-2">{{ stat.value }}</div>
            <div class="text-gray-400">{{ stat.label }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="py-20">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <h2 class="text-4xl font-bold text-white mb-4">
            Why Choose Syniac
          </h2>
          <p class="text-xl text-gray-400 max-w-2xl mx-auto">
            Experience the difference with our enterprise-grade cloud solutions designed for the modern world
          </p>
        </div>

        <div class="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div
            v-for="(feature, index) in features"
            :key="feature.title"
            class="gradient-card rounded-2xl p-8 hover-lift group"
            :style="{ animationDelay: `${index * 0.1}s` }"
          >
            <div class="mb-6">
              <div :class="[
                'inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br',
                feature.gradient,
                'group-hover:scale-110 transition-transform duration-300'
              ]">
                <i :class="[feature.icon, 'text-2xl text-white']"></i>
              </div>
            </div>
            <h3 class="text-xl font-semibold text-white mb-4">{{ feature.title }}</h3>
            <p class="text-gray-400 leading-relaxed">{{ feature.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Performance Metrics -->
    <section class="py-20 bg-white/5 backdrop-blur-sm">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <h2 class="text-4xl font-bold text-white mb-4">Performance That Matters</h2>
          <p class="text-xl text-gray-400">See how we stack up against traditional solutions</p>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          <div class="gradient-card rounded-2xl p-8 text-center hover-lift">
            <div class="text-4xl font-bold gradient-text mb-2">52%</div>
            <div class="text-lg font-semibold text-white mb-2">Faster Response</div>
            <p class="text-gray-400">Average response time of 17ms vs industry standard 36ms</p>
          </div>
          <div class="gradient-card rounded-2xl p-8 text-center hover-lift">
            <div class="text-4xl font-bold gradient-text mb-2">53%</div>
            <div class="text-lg font-semibold text-white mb-2">Better Efficiency</div>
            <p class="text-gray-400">Optimized resource utilization with advanced workload management</p>
          </div>
          <div class="gradient-card rounded-2xl p-8 text-center hover-lift">
            <div class="text-4xl font-bold gradient-text mb-2">65%</div>
            <div class="text-lg font-semibold text-white mb-2">Cost Savings</div>
            <p class="text-gray-400">Significant cost reduction through efficient resource allocation</p>
          </div>
        </div>
        
        <div class="gradient-card rounded-2xl p-8">
          <div class="h-80">
            <Line :data="performanceData" :options="chartOptions" />
          </div>
        </div>
      </div>
    </section>

    <!-- Technology Stack -->
    <section class="py-20">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <h2 class="text-4xl font-bold text-white mb-4">Built on Cutting-Edge Technology</h2>
          <p class="text-xl text-gray-400">Enterprise-grade infrastructure powered by the latest innovations</p>
        </div>

        <div class="grid gap-12 lg:grid-cols-3">
          <div v-for="category in techStack" :key="category.category" class="space-y-6">
            <h3 class="text-2xl font-bold gradient-text text-center">{{ category.category }}</h3>
            <div class="space-y-4">
              <div v-for="item in category.items" :key="item.name"
                class="gradient-card rounded-2xl p-6 hover-lift group"
              >
                <div class="flex items-center space-x-4">
                  <div class="flex-shrink-0">
                    <div class="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <i :class="[item.icon, 'text-xl text-white']"></i>
                    </div>
                  </div>
                  <div>
                    <h4 class="text-lg font-semibold text-white">{{ item.name }}</h4>
                    <p class="text-gray-400">{{ item.description }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-20 bg-gradient-to-r from-primary-600/20 to-secondary-600/20">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 class="text-4xl font-bold text-white mb-6">Ready to Transform Your Infrastructure?</h2>
        <p class="text-xl text-gray-300 mb-8">Join thousands of companies already using Syniac to power their digital transformation</p>
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="https://billing.syniac.com"
            class="btn-primary group"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span>Start Free Trial</span>
            <i class="fas fa-rocket ml-2 group-hover:translate-x-1 transition-transform duration-200"></i>
          </a>
          <RouterLink
            to="/contact"
            class="btn-secondary group"
          >
            <span>Talk to Sales</span>
            <i class="fas fa-arrow-right ml-2 group-hover:translate-x-1 transition-transform duration-200"></i>
          </RouterLink>
        </div>
      </div>
    </section>
  </div>
</template>